import { useCallback, useEffect, useState } from 'react';
import {
  IconLoader,
  Select,
  ResponseError,
  responseErrorsResolver,
  ButtonPrimary,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  useLazyGetPSPDetailsQuery,
  useEditPSPDetailsMutation,
} from './store/services/details';
import { PSPDetailsKeys } from './types';

import cn from './styles.module.css';

const StatementsSettings = () => {
  const {
    settings: {
      details: { pspDetails },
    },
  } = useAppSelector((state) => state);
  const [editError, setEditError] = useState('');
  const [statementsSource, setStatementsSource] = useState('');

  const [getCardSettings, { isLoading }] = useLazyGetPSPDetailsQuery();
  const [edit, { isLoading: editIsLoading }] = useEditPSPDetailsMutation();

  const onSave = async () => {
    setEditError('');

    const response = (await edit({
      statementsAccountDetailsSource: statementsSource,
      key: PSPDetailsKeys.STATEMENTS,
    })) as { error?: ResponseError };

    if (response?.error) {
      responseErrorsResolver({
        errors: response.error,
        setError: () => {},
        setGlobalError: setEditError,
      });
    }
  };

  const geStatementsSettingsAction = useCallback(async () => {
    const response = await getCardSettings();

    if (response && response.data) {
      const statementsSettings = response?.data?.data?.edges?.find(
        (item) => item?.node.key === PSPDetailsKeys.STATEMENTS,
      );

      if (statementsSettings) {
        setStatementsSource(statementsSettings?.node?.data?.value as string);
      }
    }
  }, []);

  useEffect(() => {
    if (!pspDetails) {
      geStatementsSettingsAction();
    } else {
      const statementsSetting = pspDetails?.find(
        (item) => item?.key === PSPDetailsKeys.STATEMENTS,
      );

      if (statementsSetting) {
        setStatementsSource(statementsSetting.data?.value as string);
      }
    }
  }, [pspDetails]);

  useEffect(() => {
    let title = 'Settings: Statement';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content = (
    <div className={cn.cardsSettingsLoader}>
      <IconLoader />
    </div>
  );

  if (!isLoading) {
    content = (
      <div className={cn.topUpSettingsWrapper}>
        <Select
          name="statementsAccountDetailsSource"
          value={statementsSource}
          list={[
            {
              name: 'Internal account',
              value: 'INTERNAL_ACCOUNT',
            },
            {
              name: 'External account',
              value: 'EXTERNAL_ACCOUNT',
            },
          ]}
          onChange={(e) => setStatementsSource(e.target.value as string)}
          title="Account number in PDF statements"
          placeholder="Select option..."
        />
        {editError && <div className={cn.cardsEditError}>{editError}</div>}
        <ButtonPrimary
          text={editIsLoading ? 'Loading...' : 'Save'}
          onClick={onSave}
          className={`${cn.saveButton} ${cn.saveStatementSettingsButton}`}
          disabled={editIsLoading}
          bold
        />
      </div>
    );
  }

  return <div className={cn.cardsSettingsWrapper}>{content}</div>;
};

export default StatementsSettings;
